import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Loader2, CheckCircle2, CalendarClock, BadgeCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { ReglesPaiement } from './ReglesPaiement';
import { EtatAttestation } from './EtatAttestation';
import { PrelevementAutoOption } from './PrelevementAutoOption';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/member-invitation`;
const fmt = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

const Step = ({ n, title, done, children, testId }) => (
  <div className={`rounded-lg border p-4 space-y-2 ${done ? 'border-emerald-200 bg-emerald-50/60' : 'border-violet-200 bg-white'}`} data-testid={testId}>
    <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
      {done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <span className="h-5 w-5 rounded-full bg-violet-600 text-white text-[11px] flex items-center justify-center">{n}</span>}
      {title}
    </p>
    {children}
  </div>
);

export const SouscriptionPaiement = ({ token, message }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [auto, setAuto] = useState(false);
  const load = useCallback(() => axios.get(`${API}/${token}/souscription`).then((r) => setS(r.data)).catch(() => setS(false)), [token]);
  useEffect(() => { load(); }, [load]);

  const go = async (key, url, body) => {
    if (!s.regles_acceptees_at && !accepted && !s.echeances?.length) { toast.error('Veuillez lire et accepter les règles de paiement'); return; }
    setBusy(key);
    try { const { data } = await axios.post(url, { origin_url: window.location.origin, regles_acceptees: accepted || !!s.regles_acceptees_at, ...body }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'ouverture du paiement"); setBusy(null); }
  };

  if (s === null) return <Loader2 className="h-6 w-6 animate-spin text-violet-600 mx-auto" />;
  if (s === false) return (
    <div className="space-y-2">
      {message && <p className="text-sm rounded p-3 bg-amber-50 text-amber-800" data-testid="souscription-paiement-message">{message.text}</p>}
      <p className="text-sm text-slate-500" data-testid="souscription-indisponible">Aucune souscription à finaliser pour le moment.</p>
    </div>
  );
  const next = (s.echeances || []).find((e) => e.statut !== 'paye');
  const acompte = Math.round(s.montant_ttc * 60) / 100;
  const rcds = (mode) => go(mode, `${API}/${token}/souscription/checkout`, { mode, prelevement_auto: mode === '4x' && auto });
  const locked = !s.regles_acceptees_at && !accepted && !s.echeances?.length;
  const resilie = s.etat_paiement === 'resilie' && s.regularisation;

  return (
    <div className="space-y-3 text-left" data-testid="souscription-paiement">
      {message && <p className={`text-sm rounded p-3 ${message.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`} data-testid="souscription-paiement-message">{message.text}</p>}
      <div className="rounded-lg bg-violet-50 p-3 text-sm">
        <p className="font-semibold text-violet-900" data-testid="souscription-produit">{s.produit_label}</p>
        <p className="text-slate-600">{s.raison_sociale} — contribution : <strong className="text-violet-700" data-testid="souscription-montant">{fmt(s.montant_ttc)}</strong></p>
      </div>
      <EtatAttestation s={s} />
      {!s.paiement_valide && <ReglesPaiement accepted={accepted} acceptedAt={s.regles_acceptees_at} onChange={setAccepted} />}
      <Step n={1} title={`Adhésion FTPE — ${fmt(s.ftpe_montant)} (paiement unique)`} done={s.ftpe_paye} testId="step-ftpe">
        {s.ftpe_paye ? <p className="text-xs text-emerald-700" data-testid="ftpe-paye">Adhésion réglée.</p> : (
          <Button onClick={() => go('ftpe', `${API}/${token}/ftpe/checkout`, {})} disabled={!!busy || locked} className="w-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="ftpe-pay-btn">
            {busy === 'ftpe' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />} Payer l'adhésion FTPE par carte ({fmt(s.ftpe_montant)})
          </Button>
        )}
      </Step>
      <Step n={2} title="Contribution RCDS" done={s.paiement_valide} testId="step-rcds">
        {s.paiement_valide ? <p className="flex items-center gap-1 text-xs text-emerald-700" data-testid="rcds-paye"><BadgeCheck className="h-4 w-4" /> Contribution intégralement réglée — souscription validée.</p>
          : !s.ftpe_paye ? <p className="text-xs text-slate-500" data-testid="rcds-attente-ftpe">Disponible après le règlement de l'adhésion FTPE.</p>
          : s.echeances?.length ? (
            <>
              <p className="text-xs text-slate-600">Paiement en 4 fois : {fmt(s.paye_ttc)} réglés, reste {fmt(s.reste_ttc)}.</p>
              <ol className="space-y-1" data-testid="rcds-echeancier">
                {s.echeances.map((e, i) => (
                  <li key={i} className="flex justify-between text-xs" data-testid={`rcds-echeance-${i}`}>
                    <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3 text-slate-400" />{i === 0 ? 'Acompte 60 %' : `Échéance ${i + 1}/4`} — {fmtD(e.date_echeance)}</span>
                    <span className={e.statut === 'paye' ? 'text-emerald-700 font-medium' : ''}>{fmt(e.montant)}{e.statut === 'paye' ? ' ✓' : ''}</span>
                  </li>
                ))}
              </ol>
              {s.prelevement_auto?.actif && <PrelevementAutoOption actif carte={s.prelevement_auto.carte} />}
              {resilie ? (
                <Button onClick={() => rcds('4x')} disabled={!!busy} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="rcds-regul-btn">
                  {busy === '4x' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />} Régulariser et réactiver mon contrat ({fmt(s.regularisation.total)})
                </Button>
              ) : next && (
                <Button onClick={() => rcds('4x')} disabled={!!busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rcds-echeance-btn">
                  {busy === '4x' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />} Payer l'échéance ({fmt(next.montant)})
                </Button>
              )}
            </>
          ) : (
            <>
              <Button onClick={() => rcds('total')} disabled={!!busy || locked} className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white" data-testid="rcds-pay-total-btn">
                {busy === 'total' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />} Souscrire et payer en une fois ({fmt(s.montant_ttc)})
              </Button>
              {s.eligible_4x && <PrelevementAutoOption checked={auto} onChange={setAuto} />}
              {s.eligible_4x && (
                <Button variant="outline" onClick={() => rcds('4x')} disabled={!!busy || locked} className="w-full h-auto py-2 whitespace-normal border-emerald-300 text-emerald-800" data-testid="rcds-pay-4x-btn">
                  {busy === '4x' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Payer en 4 fois par carte : acompte 60 % ({fmt(acompte)}) puis 3 × {fmt((s.montant_ttc - acompte) / 3)}
                </Button>
              )}
              <p className="text-[11px] text-slate-500">{s.eligible_4x ? 'Paiement en 4 fois : échéances à 30, 60 et 90 jours, réglées depuis cette même page.' : 'Paiement en une fois jusqu\'à 2 500 € ; paiement en 4 fois à partir de 2 501 €.'}</p>
            </>
          )}
      </Step>
    </div>
  );
};
